

import React, { useState, useEffect } from 'react';
import styles from './Sidebar.module.css';
import { CloudDownloadOutlined, DeleteOutlined, DownloadOutlined, CloudUploadOutlined, UserOutlined, CalendarOutlined  } from '@ant-design/icons';
import { exportDiagramAsPdf } from './hooks/pdfUtils';   // ✅ import
import UplaodDocument from '../EDM/Documents/UploadDoc';
import { fetchWithAuth } from '../../utils/api';
import { Select } from "antd";


const { Option } = Select;
const FILL_COLORS = ['transparent', '#fdeaea', '#d3f9d8', '#d0ebff', '#fff3bf', '#e5dbff'];

const TABS = [
  { id: 'mine', label: 'My Drafts' },
  // { id: 'shared', label: 'Shared Drafts' },
  { id: 'documents', label: 'Documents' },
];

function fmt(ts) {
  const date = new Date(ts);
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

// Assumption: files without a `shared` flag and without type === 'document'
// are treated as "My Drafts". Adjust this predicate once your file objects
// carry real ownership/sharing/document metadata.
function getFileTab(file) {
  if (file.type === "document") return "documents";
  if (file.shared) return "shared";
  return "mine";
}

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </svg>
);

const FolderIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h5l2 2h11v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z" />
  </svg>
);

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2.5M12 19.5V22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M2 12h2.5M19.5 12H22M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8" />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

const DocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

export default function Sidebar({
  open,
  onToggle,
  files,
  currentFileId,
  onNewFile,
  onLoadFile,
  onDeleteFile,
  onGetFileData,
  theme,
  onThemeChange,
  showGrid,
  handleGridToggle,
  autoArrowEnabled,
  onToggleAutoArrow,
  onChangeCanvasBg,
  onUploadToEdm,
  draftFiles,
  edmDocuments,
  fetchEDMDocuments,
  onLoadDocument,
}) {

  const [exportMenuFileId, setExportMenuFileId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('mine');
  const [docTypeFilter, setDocTypeFilter] = useState('All types');
  const [uploadedByFilter, setUploadedByFilter] = useState('All uploaders');
  const [docTypeOptions, setDocTypeOptions] = useState([]);
  const [uploadedByOptions, setUploadedByOptions] = useState([]);
  const [sessionUserData, setsessionUserData] = useState({});

  const visibleFiles = files.filter((file) => getFileTab(file) === activeTab);
  const visibleDrafts = draftFiles;
  const visibleDocuments = edmDocuments;


  const handleDownloadLocal = async (file) => {
    setExporting(true);
    setExportMenuFileId(null);
    try {
      // Get latest nodes+connections for this file
      const data = onGetFileData(file.id);
      const doc = await exportDiagramAsPdf({
        name: file.name || 'diagram',
        nodes: data?.nodes || file.nodes || [],
        connections: data?.connections || file.connections || [],
      });
      doc.save(`${file.name || 'diagram'}.pdf`);
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };

  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setsessionUserData(userData);
    }

    // const storedModule = JSON.parse(localStorage.getItem("ModuleData"));
    // const moduleId = storedModule?.Id?.toString();
    // setSessionModuleId(moduleId);
  }, []);

  const fetchUsers = async () => {
    try {
      const sessionDDL = sessionStorage.getItem("ddlDocumentFilters");

      if (sessionDDL) {
        const parsed = JSON.parse(sessionDDL);
        setUploadedByOptions(parsed.users || []);
        return;
      }

      const response = await fetchWithAuth(
        `ADMINRoutes/CWIGetDDLItems?OrgId=${sessionUserData?.OrgId}&UserId=0`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      const users = data.ResultData.filter(
        (item) => item.DDLName === "Users"
      );

      setUploadedByOptions(users);

      sessionStorage.setItem(
        "ddlDocumentFilters",
        JSON.stringify({
          users,
        })
      );
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUploadedByOptions([]);
    }
  };

  const fetchContentTypes = async () => {
    try {
      const response = await fetchWithAuth(
        `EDM/GetUserDocTypePermissions?OrgId=${sessionUserData?.OrgId}&UserId=${sessionUserData?.Id}&MasterTypeId=0&Type=DocTypes`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      const flowChartTypes = (data?.ResultData || []).filter(
        (item) => item.IsFlowChart === true
      );

      setDocTypeOptions(flowChartTypes);

    } catch (error) {
      console.error("Failed to fetch document types:", error);
      setDocTypeOptions([]);
    }
  };

  useEffect(() => {
    if (sessionUserData?.OrgId && sessionUserData?.Id) {
      fetchUsers();
      fetchContentTypes();
    }
  }, [sessionUserData]);

  useEffect(() => {
    fetchEDMDocuments(
      docTypeFilter === "All types" ? 0 : docTypeFilter
    );
  }, [docTypeFilter, fetchEDMDocuments]);

  return (
    <>

<div className="d-flex align-items-center gap-2">
  <button
    type="button"
    className={styles.menuButton}
    onClick={onToggle}
    aria-label="Open menu"
  >
    <MenuIcon />
  </button>

</div>

      {open && <button type="button" className={styles.backdrop} onClick={onToggle} aria-label="Close menu" />}

      <aside className={[styles.drawer, open ? styles.open : ''].join(' ')}>
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Files</div>

          <button type="button" className={styles.primaryAction} onClick={onNewFile}>
            <FolderIcon />
            <span>Create New</span>
          </button>

          <div className={styles.tabRow} role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={[styles.tabBtn, activeTab === tab.id ? styles.tabBtnActive : ''].join(' ')}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'documents' && (
            <div className={styles.filterRow}>
              <Select
                value={docTypeFilter}
                onChange={(value) => {
                  setDocTypeFilter(value);
                  fetchEDMDocuments(value === "All types" ? 0 : value);
                }}
                style={{ width: "100%" }}
                placeholder="Select Document Type"
              >
                {/* <Option value="All types">All types</Option> */}

                {docTypeOptions.map((item) => (
                  <Option
                    key={item.MasterTypeId}
                    value={item.MasterTypeId}
                  >
                    {item.TypeName}
                  </Option>
                ))}
              </Select>

              {/* <Select
                value={uploadedByFilter}
                onChange={(value) => setUploadedByFilter(value)}
                style={{ width: "100%" }}
                placeholder="Select User"
                disabled={true}
              >
                <Option value="All uploaders">All uploaders</Option>

                {uploadedByOptions.map((item) => (
                  <Option
                    key={item.ItemId}
                    value={item.ItemId}
                  >
                    {item.ItemValue}
                  </Option>
                ))}
              </Select> */}
            </div>
          )}

          <div className={styles.fileList}>
            {activeTab !== 'documents' && visibleDrafts?.length === 0 && (
              <div className={styles.empty}>
                {activeTab === 'mine' && 'No saved files yet'}
                {activeTab === 'shared' && 'No shared drafts yet'}
              </div>
            )}

            {activeTab === 'documents' && visibleDocuments?.length === 0 && (
              <div className={styles.empty}>No documents match these filters</div>
            )}

            {activeTab !== 'documents' && visibleDrafts?.map((file) => (
              <div
                key={file.id}
                className={[styles.fileItem, currentFileId === file.id ? styles.fileItemActive : ''].join(' ')}
                style={{ position: 'relative' }}
              >
                <button
                  type="button"
                  className={styles.fileMain}
                  onClick={() => { onLoadFile(file.id); onToggle(); }}
                >
                  <span className={styles.fileName}>{file.name || 'Untitled diagram'}</span>
                  <span className={styles.fileMeta}>{fmt(file.updatedAt)}</span>
                </button>

                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => onDeleteFile(file.id)}
                  aria-label="Delete file"
                >
                  <DeleteOutlined />
                </button>

                {/* ✅ Export button with popup */}
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label="Export file"
                  onClick={() => setExportMenuFileId(exportMenuFileId === file.id ? null : file.id)}
                >
                  <CloudDownloadOutlined />
                </button>

                {/* ✅ Export popup menu */}
                {exportMenuFileId === file.id && (
                  <div className={styles.exportMenu}>
                    <button
                      type="button"
                      className={styles.exportMenuItem}
                      onClick={() => handleDownloadLocal(file)}
                      disabled={exporting}
                    >
                      <DownloadOutlined style={{ marginRight: 6 }} />
                      {exporting ? 'Exporting...' : 'Download to Local'}
                    </button>
                    <button
                      type="button"
                      className={styles.exportMenuItem}
                      onClick={() => {
                        setExportMenuFileId(null);
                        onUploadToEdm(file);  // ✅ use the prop directly, not handleUploadToEdm
                      }}
                    >
                      <CloudUploadOutlined style={{ marginRight: 6 }} />
                      Upload to EDM
                    </button>
                  </div>
                )}
              </div>
            ))}

            {activeTab === 'documents' && visibleDocuments?.map((doc) => (
              <div
                key={doc.id}
                className={styles.docItem}
                onClick={() => {
                  onLoadDocument(doc);
                  onToggle();
                }}
              >
                <div className={styles.docIconWrap}>
                  <DocIcon />
                </div>
                <div className={styles.docBody}>
                  <span className={styles.fileName}>{doc.name}</span>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span className="badge bg-light text-dark border d-flex align-items-center">
                      <UserOutlined className="me-1 text-primary" />
                      {doc.uploadedBy || "Unknown"}
                    </span>

                    <span className="badge bg-light text-dark border d-flex align-items-center">
                      <CalendarOutlined className="me-1 text-primary" />
                      {new Date(doc.uploadedOn.replace("Z", "")).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Theme</div>

          <div className={styles.themeRow}>
            <button
              type="button"
              className={[styles.themeBtn, theme === 'light' ? styles.themeBtnActive : ''].join(' ')}
              onClick={() => onThemeChange('light')}
            >
              <SunIcon />
              <span>Light</span>
            </button>

            <button
              type="button"
              className={[styles.themeBtn, theme === 'dark' ? styles.themeBtnActive : ''].join(' ')}
              onClick={() => onThemeChange('dark')}
            >
              <MoonIcon />
              <span>Dark</span>
            </button>
          </div>
        </div>

        <div className={styles.section}>
          <button type="button" className={styles.toggleRow} onClick={handleGridToggle}>
            <span>Grid</span>
            <span className={[styles.toggleCheck, showGrid ? styles.toggleCheckActive : ''].join(' ')}>OK</span>
          </button>
        </div>

        <div className={styles.section}>
          <button type="button" className={styles.toggleRow} onClick={onToggleAutoArrow}>
            <span>Auto Arrow</span>
            <span className={[styles.toggleCheck, autoArrowEnabled ? styles.toggleCheckActive : ''].join(' ')}>OK</span>
          </button>
        </div>

        <div className={styles.section}>
          <div className={styles.bgSection}>
            <div className={styles.bgLabel}>Canvas Background</div>

            <div className={styles.bgSwatches}>
              {FILL_COLORS.map((color) => (
                <button
                  key={color}
                  className={[
                    styles.bgSwatch,
                    color === 'transparent' ? styles.bgSwatchTransparent : '',
                  ].join(' ')}
                  style={color !== 'transparent' ? { background: color } : undefined}
                  onClick={() => onChangeCanvasBg(color === 'transparent' ? 'transparent' : color)}
                />
              ))}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}